import {useEffect, useId, useRef} from 'react'

import {type DitherParams, useWebGLContext} from '../WebGLContext/WebGLContext'

interface WebGLDitherProps {
  className?: string
  params: DitherParams
  /**
   * Opt into compensating the WebGL canvas transform for the WebGLPage
   * wrapper's viewport offset. See `DitherRegistration.offsetByWrapperTop`.
   */
  offsetByWrapperTop?: boolean
}

export function WebGLDither({className, params, offsetByWrapperTop}: WebGLDitherProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isRegisteredRef = useRef(false)
  const instanceRef = useRef<number>(0)

  const {registerDither, unregisterDither} = useWebGLContext()
  const id = useId()

  const getInstanceId = () => `${id}-${instanceRef.current}`

  useEffect(() => {
    if (!ref.current) return
    if (isRegisteredRef.current) {
      unregisterDither(getInstanceId())
      isRegisteredRef.current = false
    }

    instanceRef.current++

    registerDither({id: getInstanceId(), element: ref.current, params, offsetByWrapperTop})
    isRegisteredRef.current = true
  }, [params.color, params.type, params.fadeIn, params.dotOpacity, offsetByWrapperTop])

  useEffect(() => {
    return () => {
      if (isRegisteredRef.current) {
        unregisterDither(getInstanceId())
        isRegisteredRef.current = false
      }
    }
  }, [])

  return <div ref={ref} className={className} />
}
